/**
 * needs-attention — one row per PERSON in the Mission Control queue.
 *
 * Mission Control shows "the highest-priority work across your sales queue" as the ten oldest
 * pending drafts. Drafts, not people. On 12.08.2026 that surfaced Marina Mijatović three times in a
 * five-item list — three drafts on the same conversation, 4443h / 4438h / 4437h old, all three
 * already SUPERSEDED by a newer reply.
 *
 * Two independent faults produced that, and both are fixed at their own layer:
 *
 *   1. The query never excluded superseded drafts. `is_actionable_pending_draft(status,
 *      is_superseded)` has been the shared definition of "actionable" in SQL all along, and the
 *      Reply Queue has always filtered `.not('is_superseded','is',true)`. Mission Control checked
 *      only `status = 'pending_approval'`, so a draft that a newer inbound had already replaced
 *      stayed on the list forever. That filter now lives in the query.
 *
 *   2. Even correctly filtered, one person can hold several live drafts across several threads, and
 *      a list of work-to-do should name them once. That is this module.
 *
 * The identity key is deliberately layered rather than "contact_id": the same human can exist as two
 * contact rows (Marina does — her two live drafts sit on two threads under two contacts), so the
 * thread is the unit of conversation, and the lead identifier and finally the name catch rows where
 * the thread link is missing.
 */

export interface AttentionCandidate {
  id: string;
  leadName: string;
  threadId?: string | null;
  leadId?: string | null;
  /** ISO timestamp. The oldest wins, because waiting longest is the priority signal. */
  timestamp: string;
}

/**
 * Stable identity for "the same person's conversation". Falls through thread → lead id → name.
 *
 * Name is the last resort and is imperfect by construction — two different people can share one —
 * but it only applies to rows that have neither a thread nor a lead id, which are broken rows. It
 * is better to under-report a broken row than to show it three times.
 */
export const attentionIdentity = (item: AttentionCandidate): string => {
  if (item.threadId) return `thread:${item.threadId}`;
  if (item.leadId) return `lead:${item.leadId}`;
  return `name:${item.leadName.trim().toLowerCase()}`;
};

/**
 * dedupeByPerson — keep the OLDEST candidate per person, preserving the caller's ordering.
 *
 * Oldest rather than newest: the list is sorted by how long something has been waiting, and
 * collapsing a person onto their newest draft would silently reset that clock and move them down
 * the queue — the opposite of what the surface is for.
 */
export const dedupeByPerson = <T extends AttentionCandidate>(items: readonly T[]): T[] => {
  const oldestByPerson = new Map<string, T>();

  for (const item of items) {
    const key = attentionIdentity(item);
    const existing = oldestByPerson.get(key);
    if (!existing) {
      oldestByPerson.set(key, item);
      continue;
    }
    const existingAt = new Date(existing.timestamp).getTime();
    const candidateAt = new Date(item.timestamp).getTime();
    // Ties break on id so the choice is deterministic across reloads rather than input-order
    // dependent — the same reason compareDraftRecency in live-draft.ts breaks ties on id.
    if (candidateAt < existingAt || (candidateAt === existingAt && item.id < existing.id)) {
      oldestByPerson.set(key, item);
    }
  }

  const winners = new Set([...oldestByPerson.values()].map((i) => i.id));
  return items.filter((i) => winners.has(i.id));
};
